import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Globe,
  Loader2,
  Check,
  Package,
  Upload,
  AlertTriangle,
  ArrowRight,
  ImageOff,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  Search,
} from 'lucide-react';
import { productImportApi, type ImportCategory, type ImportProductPreview } from '../../api/endpoints/productImport';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { useToast } from '../../components/ui/Alert';

type Phase = 'url' | 'categories' | 'running';

export function ProductImportWizard() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<Phase>('url');
  const [storeUrl, setStoreUrl] = useState('');
  const [importId, setImportId] = useState<string | null>(null);
  const [categories, setCategories] = useState<ImportCategory[]>([]);
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());
  const [catSearch, setCatSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [conflictPolicy, setConflictPolicy] = useState<'skip' | 'create'>('skip');
  const [parentName, setParentName] = useState('');
  const [productSelectionInit, setProductSelectionInit] = useState(false);

  // ── Step 1: inspect the store URL ───────────────────────────────────────
  const inspectMutation = useMutation({
    mutationFn: (url: string) => productImportApi.inspect(storeId!, url),
    onSuccess: (data) => {
      setImportId(data.importId);
      setCategories(data.categories);
      setSelectedCats(new Set(data.categories.map((c) => c.id))); // default: all
      setPhase('categories');
    },
    onError: (err: any) => toast('error', err?.message || t('productImport.inspectError')),
  });

  // ── Step 2: start the crawl ─────────────────────────────────────────────
  const crawlMutation = useMutation({
    mutationFn: () => productImportApi.crawl(storeId!, importId!, [...selectedCats]),
    onSuccess: () => {
      setPhase('running');
      queryClient.invalidateQueries({ queryKey: ['product-import', storeId, importId] });
    },
    onError: (err: any) => toast('error', err?.message || t('common.error')),
  });

  // ── Step 4: run the import ──────────────────────────────────────────────
  const runMutation = useMutation({
    mutationFn: () => productImportApi.run(storeId!, importId!, [...selectedProducts], conflictPolicy, parentName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['product-import', storeId, importId] }),
    onError: (err: any) => toast('error', err?.message || t('common.error')),
  });

  // ── Job polling (active only while crawling/importing) ──────────────────
  const { data: job } = useQuery({
    queryKey: ['product-import', storeId, importId],
    queryFn: () => productImportApi.status(storeId!, importId!),
    enabled: !!importId && phase === 'running',
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'crawling' || s === 'importing' ? 1500 : false;
    },
  });

  // Once the crawl is ready, default-select every non-conflicting product.
  useEffect(() => {
    if (job?.status === 'ready' && !productSelectionInit) {
      setSelectedProducts(new Set(job.products.filter((p) => !p.conflict).map((p) => p.sourceId)));
      setProductSelectionInit(true);
    }
  }, [job?.status, job?.products, productSelectionInit]);

  // Build a parent→children index so toggling a category toggles its subtree.
  const childrenOf = useMemo(() => {
    const map = new Map<string, ImportCategory[]>();
    for (const c of categories) {
      if (c.parentId) {
        if (!map.has(c.parentId)) map.set(c.parentId, []);
        map.get(c.parentId)!.push(c);
      }
    }
    return map;
  }, [categories]);

  const roots = useMemo(() => categories.filter((c) => !c.parentId), [categories]);

  const nodeById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // When searching, flatten to categories whose name matches (case-insensitive).
  const filteredCats = useMemo(() => {
    const q = catSearch.trim().toLowerCase();
    if (!q) return null;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [catSearch, categories]);

  // Build a "root › … › name" breadcrumb so a matched sub-category has context.
  function categoryPath(c: ImportCategory): string[] {
    const parts = [c.name];
    let cur = c;
    const guard = new Set<string>();
    while (cur.parentId && nodeById.has(cur.parentId) && !guard.has(cur.id)) {
      guard.add(cur.id);
      cur = nodeById.get(cur.parentId)!;
      parts.unshift(cur.name);
    }
    return parts;
  }

  // "Select all" acts on whatever is currently visible (all, or the filtered set).
  const visibleCatIds = filteredCats ? filteredCats.map((c) => c.id) : categories.map((c) => c.id);
  const allVisibleSelected = visibleCatIds.length > 0 && visibleCatIds.every((id) => selectedCats.has(id));

  function toggleAllVisible() {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const id of visibleCatIds) next.delete(id);
      else for (const id of visibleCatIds) next.add(id);
      return next;
    });
  }

  function descendantsOf(id: string): string[] {
    const out: string[] = [];
    const stack = [...(childrenOf.get(id) || [])];
    while (stack.length) {
      const c = stack.pop()!;
      out.push(c.id);
      stack.push(...(childrenOf.get(c.id) || []));
    }
    return out;
  }

  function toggleCategory(id: string) {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      const ids = [id, ...descendantsOf(id)];
      const turningOn = !prev.has(id);
      for (const cid of ids) {
        if (turningOn) next.add(cid);
        else next.delete(cid);
      }
      return next;
    });
  }

  function toggleProduct(sourceId: string) {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) next.delete(sourceId);
      else next.add(sourceId);
      return next;
    });
  }

  // ── Render helpers ──────────────────────────────────────────────────────
  const header = (
    <div className="mb-6 flex items-center gap-3">
      <button
        onClick={() => navigate(`/${storeId}/products`)}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border-default text-text-secondary hover:bg-bg-hover"
        title={t('common.back', 'رجوع')}
      >
        <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
      </button>
      <div>
        <h1 className="text-xl font-bold">{t('productImport.title')}</h1>
        <p className="mt-0.5 text-sm text-text-secondary">{t('productImport.subtitle')}</p>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl">
      {header}

      {/* Step 1: URL */}
      {phase === 'url' && (
        <div className="rounded-xl border border-border-default bg-bg-secondary p-6">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10">
            <Globe className="h-6 w-6 text-accent" />
          </div>
          <h2 className="mb-1 text-lg font-semibold">{t('productImport.urlStepTitle')}</h2>
          <p className="mb-4 text-sm text-text-secondary">{t('productImport.urlStepDesc')}</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (storeUrl.trim()) inspectMutation.mutate(storeUrl.trim());
            }}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <div className="flex-1">
              <Input
                type="url"
                dir="ltr"
                placeholder="https://store.salla.sa"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                required
              />
            </div>
            <Button type="submit" isLoading={inspectMutation.isPending} disabled={!storeUrl.trim()}>
              {t('productImport.inspectBtn')}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Button>
          </form>
          <p className="mt-3 text-xs text-text-muted">{t('productImport.urlHint')}</p>
        </div>
      )}

      {/* Step 2: category selection */}
      {phase === 'categories' && (
        <div className="rounded-xl border border-border-default bg-bg-secondary p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t('productImport.pickCategories')}</h2>
              <p className="mt-0.5 text-sm text-text-secondary">
                {t('productImport.foundCategories', { count: categories.length })}
              </p>
            </div>
            <button onClick={toggleAllVisible} className="text-sm font-medium text-accent hover:underline">
              {allVisibleSelected ? t('productImport.deselectAll') : t('productImport.selectAll')}
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted start-3" />
            <input
              type="text"
              value={catSearch}
              onChange={(e) => setCatSearch(e.target.value)}
              placeholder={t('productImport.searchCategories')}
              className="w-full rounded-lg border border-border-default bg-bg-field py-2 text-sm text-text-primary outline-none transition-colors placeholder:text-text-muted focus:border-accent ps-9 pe-3"
            />
          </div>

          <div className="max-h-[50vh] space-y-1 overflow-y-auto pe-1">
            {filteredCats ? (
              filteredCats.length > 0 ? (
                filteredCats.map((c) => {
                  const path = categoryPath(c);
                  return (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-bg-hover"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCats.has(c.id)}
                        onChange={() => toggleCategory(c.id)}
                        className="h-4 w-4 rounded border-border-default accent-accent"
                      />
                      <span className="truncate text-sm">
                        {path.length > 1 && (
                          <span className="text-text-muted">{path.slice(0, -1).join(' › ')} › </span>
                        )}
                        {c.name}
                      </span>
                    </label>
                  );
                })
              ) : (
                <p className="px-2 py-6 text-center text-sm text-text-muted">{t('productImport.noMatches')}</p>
              )
            ) : (
              roots.map((root) => (
                <CategoryTree
                  key={root.id}
                  node={root}
                  childrenOf={childrenOf}
                  selected={selectedCats}
                  onToggle={toggleCategory}
                />
              ))
            )}
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-border-default pt-4">
            <span className="text-sm text-text-secondary">
              {t('productImport.selectedCount', { count: selectedCats.size })}
            </span>
            <Button
              onClick={() => crawlMutation.mutate()}
              isLoading={crawlMutation.isPending}
              disabled={selectedCats.size === 0}
            >
              {t('productImport.startCrawl')}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3+: running (crawl → preview → import → done) */}
      {phase === 'running' && job && (
        <>
          {/* Crawling */}
          {job.status === 'crawling' && (
            <div className="rounded-xl border border-border-default bg-bg-secondary p-6">
              <div className="mb-4 flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
                <div>
                  <h2 className="font-semibold">{t('productImport.crawling')}</h2>
                  <p className="text-sm text-text-secondary">
                    {t('productImport.discoveredCount', { count: job.crawl?.discovered ?? 0 })}
                  </p>
                </div>
              </div>
              <ProgressBar
                value={job.crawl?.doneCategories ?? 0}
                max={Math.max(job.crawl?.totalCategories ?? 1, 1)}
                label={t('productImport.categoriesProgress', {
                  done: job.crawl?.doneCategories ?? 0,
                  total: job.crawl?.totalCategories ?? 0,
                })}
              />
              <div className="mt-4 max-h-64 space-y-1 overflow-y-auto">
                {Object.entries(job.crawl?.perCategory ?? {}).map(([id, c]) => (
                  <div key={id} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm">
                    <span className="flex items-center gap-2">
                      <CrawlStateIcon state={c.state} />
                      <span className={c.state === 'error' ? 'text-danger' : ''}>{c.name}</span>
                    </span>
                    <span className="text-text-muted">{c.count}</span>
                  </div>
                ))}
              </div>
              {!!job.crawl?.recent?.length && (
                <div className="mt-3 truncate text-xs text-text-muted">
                  {t('productImport.lastFound')}: {job.crawl.recent[0]}
                </div>
              )}
              {!!job.crawl?.enrichTotal && (
                <div className="mt-2 text-xs text-text-muted">
                  {t('productImport.readingOptions', {
                    done: job.crawl.enrichDone ?? 0,
                    total: job.crawl.enrichTotal,
                  })}
                </div>
              )}
            </div>
          )}

          {/* Preview / ready */}
          {job.status === 'ready' && (
            <PreviewStep
              products={job.products}
              currency={job.store.currency}
              selected={selectedProducts}
              onToggle={toggleProduct}
              onToggleAll={(all) =>
                setSelectedProducts(all ? new Set(job.products.map((p) => p.sourceId)) : new Set())
              }
              conflictSummary={job.conflictSummary}
              conflictPolicy={conflictPolicy}
              setConflictPolicy={setConflictPolicy}
              parentName={parentName}
              setParentName={setParentName}
              running={runMutation.isPending}
              onRun={() => runMutation.mutate()}
            />
          )}

          {/* Importing */}
          {job.status === 'importing' && job.importStats && (
            <div className="rounded-xl border border-border-default bg-bg-secondary p-6">
              <div className="mb-4 flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-accent" />
                <h2 className="font-semibold">{t('productImport.importing')}</h2>
              </div>
              <ProgressBar
                value={job.importStats.created + job.importStats.skipped + job.importStats.failed}
                max={Math.max(job.importStats.total, 1)}
                label={t('productImport.importProgress', {
                  done: job.importStats.created + job.importStats.skipped + job.importStats.failed,
                  total: job.importStats.total,
                })}
              />
              <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
                <Stat label={t('productImport.created')} value={job.importStats.created} tone="success" />
                <Stat label={t('productImport.skipped')} value={job.importStats.skipped} tone="muted" />
                <Stat label={t('productImport.imagesFetched')} value={job.importStats.imageOk} tone="muted" />
              </div>
              {!!job.importStats.recent?.length && (
                <div className="mt-3 truncate text-xs text-text-muted">
                  {job.importStats.recent[0]}
                </div>
              )}
            </div>
          )}

          {/* Done */}
          {job.status === 'done' && job.importStats && (
            <div className="rounded-xl border border-border-default bg-bg-secondary p-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-success/10">
                <CheckCircle2 className="h-7 w-7 text-success" />
              </div>
              <h2 className="mb-1 text-lg font-semibold">{t('productImport.doneTitle')}</h2>
              <p className="mb-5 text-sm text-text-secondary">
                {t('productImport.doneSummary', {
                  created: job.importStats.created,
                  skipped: job.importStats.skipped,
                  failed: job.importStats.failed,
                })}
              </p>
              <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label={t('productImport.created')} value={job.importStats.created} tone="success" />
                <Stat label={t('productImport.skipped')} value={job.importStats.skipped} tone="muted" />
                <Stat label={t('productImport.failed')} value={job.importStats.failed} tone={job.importStats.failed ? 'danger' : 'muted'} />
                <Stat label={t('productImport.imageFails')} value={job.importStats.imageFail} tone={job.importStats.imageFail ? 'warning' : 'muted'} />
              </div>
              <Button onClick={() => navigate(`/${storeId}/products`)}>
                <Package className="h-4 w-4" />
                {t('productImport.viewProducts')}
              </Button>
            </div>
          )}

          {/* Failed */}
          {job.status === 'failed' && (
            <div className="rounded-xl border border-danger/30 bg-danger/5 p-6 text-center">
              <XCircle className="mx-auto mb-3 h-8 w-8 text-danger" />
              <h2 className="mb-1 font-semibold">{t('productImport.failedTitle')}</h2>
              <p className="mb-4 text-sm text-text-secondary">{t('productImport.failedDesc')}</p>
              <Button variant="secondary" onClick={() => { setPhase('url'); setImportId(null); }}>
                {t('productImport.tryAgain')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function CrawlStateIcon({ state }: { state: string }) {
  if (state === 'done') return <Check className="h-4 w-4 text-success" />;
  if (state === 'fetching') return <Loader2 className="h-4 w-4 animate-spin text-accent" />;
  if (state === 'error') return <AlertTriangle className="h-4 w-4 text-danger" />;
  return <span className="h-2 w-2 rounded-full bg-text-muted/40" />;
}

function CategoryTree({
  node,
  childrenOf,
  selected,
  onToggle,
}: {
  node: ImportCategory;
  childrenOf: Map<string, ImportCategory[]>;
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  const kids = childrenOf.get(node.id) || [];
  return (
    <div>
      <label
        className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-bg-hover"
        style={{ marginInlineStart: node.depth * 16 }}
      >
        <input
          type="checkbox"
          checked={selected.has(node.id)}
          onChange={() => onToggle(node.id)}
          className="h-4 w-4 rounded border-border-default accent-accent"
        />
        <span className="text-sm">{node.name}</span>
      </label>
      {kids.map((k) => (
        <CategoryTree key={k.id} node={k} childrenOf={childrenOf} selected={selected} onToggle={onToggle} />
      ))}
    </div>
  );
}

function PreviewStep({
  products,
  currency,
  selected,
  onToggle,
  onToggleAll,
  conflictSummary,
  conflictPolicy,
  setConflictPolicy,
  parentName,
  setParentName,
  running,
  onRun,
}: {
  products: ImportProductPreview[];
  currency: string;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (all: boolean) => void;
  conflictSummary: { sku: number; title: number };
  conflictPolicy: 'skip' | 'create';
  setConflictPolicy: (p: 'skip' | 'create') => void;
  parentName: string;
  setParentName: (v: string) => void;
  running: boolean;
  onRun: () => void;
}) {
  const { t } = useTranslation();
  const totalConflicts = conflictSummary.sku + conflictSummary.title;
  // How many will actually import given the conflict policy.
  const willImport = [...selected].filter((id) => {
    const p = products.find((x) => x.sourceId === id);
    return p && !(conflictPolicy === 'skip' && p.conflict);
  }).length;

  return (
    <div className="rounded-xl border border-border-default bg-bg-secondary p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t('productImport.previewTitle')}</h2>
          <p className="mt-0.5 text-sm text-text-secondary">
            {t('productImport.previewCount', { count: products.length })}
          </p>
        </div>
        <button
          onClick={() => onToggleAll(selected.size !== products.length)}
          className="text-sm font-medium text-accent hover:underline"
        >
          {selected.size === products.length ? t('productImport.deselectAll') : t('productImport.selectAll')}
        </button>
      </div>

      {totalConflicts > 0 && (
        <div className="mb-4 rounded-lg border border-warning/30 bg-warning/5 p-3">
          <div className="flex items-center gap-2 text-sm text-text-primary">
            <AlertTriangle className="h-4 w-4 text-warning" />
            {t('productImport.conflictsFound', { count: totalConflicts })}
          </div>
          <div className="mt-2 flex gap-4 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input type="radio" checked={conflictPolicy === 'skip'} onChange={() => setConflictPolicy('skip')} className="accent-accent" />
              {t('productImport.policySkip')}
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input type="radio" checked={conflictPolicy === 'create'} onChange={() => setConflictPolicy('create')} className="accent-accent" />
              {t('productImport.policyCreate')}
            </label>
          </div>
        </div>
      )}

      <div className="max-h-[45vh] space-y-1 overflow-y-auto">
        {products.map((p) => (
          <label
            key={p.sourceId}
            className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-bg-hover"
          >
            <input
              type="checkbox"
              checked={selected.has(p.sourceId)}
              onChange={() => onToggle(p.sourceId)}
              className="h-4 w-4 rounded border-border-default accent-accent"
            />
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-bg-tertiary">
              {p.imageUrls[0] ? (
                <img src={p.imageUrls[0]} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <ImageOff className="h-4 w-4 text-text-muted" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{p.title}</div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
                <span>{p.price} {currency}</span>
                {p.salePrice ? <span className="text-success">{p.salePrice} {currency}</span> : null}
                {p.hasVariants && p.variantTypes[0] && (
                  <span className="rounded bg-accent/10 px-1.5 py-0.5 text-accent">
                    {t('productImport.variantsBadge', { count: p.variantTypes[0].options.length })}
                  </span>
                )}
                {p.fields.length > 0 && (
                  <span className="rounded bg-bg-tertiary px-1.5 py-0.5">
                    {t('productImport.fieldsBadge', { count: p.fields.length })}
                  </span>
                )}
                {p.pricingWarning && <span className="text-warning">{t('productImport.noPrice')}</span>}
                {!p.available && !p.pricingWarning && <span className="text-text-muted">{t('productImport.outOfStock')}</span>}
              </div>
            </div>
            {p.conflict && (
              <span className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-[11px] text-warning">
                {p.conflict === 'sku' ? t('productImport.conflictSku') : t('productImport.conflictTitle')}
              </span>
            )}
          </label>
        ))}
      </div>

      {/* Optional: nest all imported categories under one parent. */}
      <div className="mt-4 border-t border-border-default pt-4">
        <label className="text-sm text-text-secondary">{t('productImport.parentLabel')}</label>
        <input
          type="text"
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          placeholder={t('productImport.parentPlaceholder')}
          className="mt-1.5 w-full rounded-lg border border-border-default bg-bg-field px-3 py-2 text-sm text-text-primary outline-none transition-colors placeholder:text-text-muted focus:border-accent"
        />
        <p className="mt-1 text-xs text-text-muted">{t('productImport.parentHint')}</p>
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border-default pt-4">
        <span className="text-sm text-text-secondary">
          {t('productImport.willImportCount', { count: willImport })}
        </span>
        <Button onClick={onRun} isLoading={running} disabled={willImport === 0}>
          <Upload className="h-4 w-4" />
          {t('productImport.runImport')}
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'success' | 'danger' | 'warning' | 'muted' }) {
  const toneClass =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-text-primary';
  return (
    <div className="rounded-lg border border-border-default bg-bg-tertiary p-3">
      <div className={`text-xl font-bold ${toneClass}`}>{value}</div>
      <div className="mt-0.5 text-xs text-text-secondary">{label}</div>
    </div>
  );
}
